/**
 * Turning field values into utility class names.
 *
 * Every layout choice a primitive exposes resolves to a class in the token registry. There is
 * no path from a field to an arbitrary CSS value, which is what keeps the export guarantee
 * intact while still letting someone build any layout they like.
 */

import type { ClassRef } from '@lp/shared';

/** Build a `lp-box` class list from a set of optional utilities. */
export function boxCls(...utils: Array<string | false | null | undefined>): Array<string | ClassRef> {
  const out: Array<string | ClassRef> = ['lp-box'];
  for (const u of utils) {
    if (u) out.push({ name: u, combo: true, parent: 'lp-box' });
  }
  return out;
}

/** Add a combo to a text class, e.g. `textCls('lp-h2', 'is-center-h2')`. */
export function textCls(
  base: string,
  ...combos: Array<string | false | null | undefined>
): Array<string | ClassRef> {
  const out: Array<string | ClassRef> = [base];
  for (const c of combos) {
    if (c) out.push({ name: c, combo: true, parent: base });
  }
  return out;
}

const SUFFIX: Record<string, string> = {
  'lp-display': 'display',
  'lp-h1': 'h1',
  'lp-h2': 'h2',
  'lp-h2-panel': 'h2-panel',
  'lp-h3': 'h3',
  'lp-h4': 'h4',
  'lp-lead': 'lead',
  'lp-body': 'body',
  'lp-body-sm': 'body-sm',
  'lp-small': 'small',
  'lp-eyebrow': 'eyebrow',
  'lp-rich-text': 'rich-text',
  'lp-list': 'list',
  'lp-list-item': 'list-item',
};

/** The alignment combo for a text base, or nothing when it is already the default. */
export function alignCombo(base: string, align: 'left' | 'center' | 'right' | undefined): string | null {
  const suffix = SUFFIX[base];
  return align && suffix ? `is-${align}-${suffix}` : null;
}

/** The inverted-colour combo for a text base on a dark background. */
export function onDarkCombo(base: string, dark: boolean): string | null {
  const suffix = SUFFIX[base];
  return dark && suffix ? `is-on-dark-${suffix}` : null;
}

/** Tones that need inverted text. */
export const DARK_TONES = new Set(['indigo', 'pink']);

export const isDark = (tone: string | undefined): boolean => DARK_TONES.has(tone ?? '');

/**
 * Spacing scale steps offered in the UI.
 *
 * Labelled with the pixel value as well as the t-shirt size. "Is Medium enough space under
 * this heading?" is a question nobody can answer from the word alone, and the whole point of
 * these controls is judging a gap by eye against a number.
 *
 * Every step of the scale is here. The list used to skip 4px, 16px and 128px, which meant the
 * only way to tighten a gap below 12px was to remove it entirely — the jump from Small to
 * None. Closing a gap slightly is exactly what someone reaches for when a heading sits too
 * far off its paragraph.
 */
export const SPACE_OPTIONS = [
  { label: 'None', value: '0' },
  { label: 'XXS — 4px', value: '1' },
  { label: 'XS — 8px', value: '2' },
  { label: 'Small — 12px', value: '3' },
  { label: 'Small+ — 16px', value: '4' },
  { label: 'Medium — 24px', value: '5' },
  { label: 'Large — 32px', value: '6' },
  { label: 'XL — 48px', value: '7' },
  { label: 'XXL — 64px', value: '8' },
  { label: 'Huge — 96px', value: '9' },
  { label: 'Giant — 128px', value: '10' },
] as const;

export const ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Centre', value: 'center' },
  { label: 'Right', value: 'right' },
] as const;

export const TONE_OPTIONS = [
  { label: 'White', value: 'white' },
  { label: 'Soft grey', value: 'surface' },
  { label: 'Indigo', value: 'indigo' },
  { label: 'Pink', value: 'pink' },
  { label: 'None', value: '' },
] as const;
